import React, { FC, ReactNode, useCallback, useContext } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import set from 'lodash/set';
import { produce } from 'immer';
import styled from 'styled-components';

import FieldContainer from './FieldContainer';
import FormContext from 'src/presentation/contexts/FormContext';
import { DropdownMenu } from 'src/presentation/components/agent_desktop_components';
import { OpenerButton } from '@ad-scripts/presentation/components/common/dropdown_menu';

type DropdownProps = {
  'data-aid'?: string;
  allowEmptyValues?: boolean;
  attr: string;
  className?: string;
  description?: string;
  disabled?: boolean;
  fitOpenerWidth?: boolean;
  isMultiSelect?: boolean;
  label: string | ReactNode;
  maxRows?: number;
  multiSelectLabel?: string;
  onChange?: Function;
  options?: any[];
  otherProps?: any;
  placeholder?: string;
  readOnly?: boolean;
  searchable?: boolean;
  value?: string | boolean;
};

const Dropdown: FC<DropdownProps & any> = ({
  'data-aid': dataAid = null,
  allowEmptyValues,
  attr,
  className,
  description,
  disabled,
  fitOpenerWidth = true,
  isMultiSelect,
  label,
  maxRows,
  multiSelectLabel,
  onChange,
  options,
  placeholder,
  readOnly,
  searchable,
  tooltip,
  value,
  ...otherProps
}) => {
  const { errors, workingState, updateState, resetError, onBlur } = useContext(FormContext);
  const error = find(errors, error => error.attr === attr);
  const onSelect = useCallback(
    val => {
      if (onChange) {
        onChange(val);
      } else {
        resetError({ attr, errors });

        const getMultiSelectValuesToUpdate = attr => {
          const values = get(workingState, attr);
          if (!values) {
            return [val];
          }

          if (!values.includes(val)) {
            return [val, ...values];
          }

          return values.filter(item => item !== val);
        };

        const valueToUpdate = isMultiSelect ? getMultiSelectValuesToUpdate(attr) : val;
        updateState(set({}, attr, valueToUpdate));
        onBlur && onBlur({ currentState: produce(workingState, draftState => set(draftState, attr, valueToUpdate)) });
      }
    },
    [attr, errors, isMultiSelect, onChange, resetError, updateState, workingState, onBlur]
  );

  const workingVal = !isNil(value) ? value : get(workingState, attr);

  return (
    <FieldContainer
      className={error ? `${className} invalid` : className}
      data-aid={dataAid}
      description={description}
      error={get(error, 'detail')}
      label={label}
      preventContentFromOverflow // prevents a dropdown with long value to overflow its container
      tooltip={tooltip}
      control={
        <DropdownMenu
          allowEmptyValues={allowEmptyValues}
          data-aid={dataAid}
          disabled={disabled}
          fitOpenerWidth={fitOpenerWidth}
          isError={!!error}
          isMultiSelect={isMultiSelect}
          maxRows={maxRows}
          multiSelectLabel={multiSelectLabel}
          onSelect={onSelect}
          options={options}
          placeholder={placeholder}
          readOnly={readOnly}
          searchable={searchable}
          value={workingVal}
          {...otherProps}
        />
      }
    />
  );
};

export const DropdownMediumWidth = styled(Dropdown).attrs({ width: '250px' })`
  ${OpenerButton} {
    width: ${p => p.width};
  }
`;

export default Dropdown;
